import {
	type DetailedHTMLProps,
	type ImgHTMLAttributes,
	useState
} from "react";

import { Dialog, DialogContent } from "src/components/ui/dialog";
import { Button } from "src/components/ui/button";
import axios from "axios";
import { useAzureImageDownload } from "src/components/AssetUploaders/useAzureImageDownload";

interface AzureImageProps extends DetailedHTMLProps<
	ImgHTMLAttributes<HTMLImageElement>,
	HTMLImageElement
> {
	uuid: string;
	defaultUrl?: string;
	imageProfile?: ImageProfile;
	linkToFull?: boolean;
	openInModal?: boolean;
	/** Accessible alt text. Pass empty string only for purely decorative images. */
	alt?: string;
}

const AzureImage = ({
	uuid,
	defaultUrl = "",
	imageProfile,
	linkToFull = false,
	openInModal = false,
	alt = "Uploaded image",
	...restProps
}: AzureImageProps) => {
	const [isOpen, setIsOpen] = useState(false);

	const downloadType = "image";

	const { url, isLoading: isDownloadingImage } = useAzureImageDownload(
		downloadType,
		defaultUrl,
		uuid,
		imageProfile
	);

	const { url: fullImageUrl } = useAzureImageDownload(
		downloadType,
		defaultUrl,
		uuid,
		imageProfile,
		"full"
	);

	const handleDownloadImageClick = async () => {
		if (!fullImageUrl) return;

		try {
			const response = await axios.get(fullImageUrl, { responseType: "blob" });

			if (!response || !response.data) {
				throw new Error("No image data received");
			}

			const blob = response.data;
			const objectUrl = URL.createObjectURL(blob);

			// Create a download link
			const link = document.createElement("a");
			link.href = objectUrl;
			link.download = uuid;

			// Append the link to the document and trigger the click event
			document.body.appendChild(link);
			link.click();

			// Remove the link after the download
			document.body.removeChild(link);

			// Clean up the Object URL
			URL.revokeObjectURL(objectUrl);
		} catch (error) {
			console.error("Error fetching and downloading image:", error);
		}
	};

	if (isDownloadingImage) {
		return (
			<div className="w-full h-full flex justify-center items-center">
				<div className="animate-spin rounded-full h-8 w-8 border-2 border-muted border-t-foreground" />
			</div>
		);
	}

	if (linkToFull) {
		const fullLink = `https://${window.location.host}/observations/image/${uuid}`;
		return (
			<a
				href={fullLink}
				title="Full image in new tab"
				target="_blank"
				rel="noreferrer"
			>
				<img src={url} alt={alt} {...restProps} />
			</a>
		);
	}

	if (openInModal) {
		return (
			<>
				<img
					src={url}
					alt={alt}
					onClick={() => setIsOpen(true)}
					className="cursor-pointer"
					{...restProps}
				/>

				<Dialog open={isOpen} onOpenChange={setIsOpen}>
					<DialogContent className="max-w-[980px] h-[672px] pt-14 px-10">
						<div className="bg-muted h-[496px]">
							<img
								src={fullImageUrl}
								alt={alt}
								{...restProps}
								style={{
									objectFit: "contain",
									width: "100%",
									maxHeight: 496
								}}
							/>
						</div>

						<div className="flex justify-center mt-10">
							<Button onClick={handleDownloadImageClick}>Download</Button>
						</div>
					</DialogContent>
				</Dialog>
			</>
		);
	}

	return <img src={url} alt={alt} {...restProps} />;
};

export { AzureImage, type AzureImageProps };
